import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  websocketService, 
  DeviceStatusPayload, 
  SensorReadingPayload, 
  ValveActionPayload,
  IrrigationEventPayload 
} from '@/services/websocket.service';
import { useAuth } from './AuthContext';
import { useToast } from '@/hooks/use-toast';

interface WebSocketContextType {
  isConnected: boolean;
  connectionState: 'connecting' | 'connected' | 'disconnected';
  connect: () => Promise<void>;
  disconnect: () => void;
  lastDeviceStatus: DeviceStatusPayload | null;
  lastSensorReading: SensorReadingPayload | null;
  lastValveAction: ValveActionPayload | null;
  lastIrrigationEvent: IrrigationEventPayload | null;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected'>('disconnected');
  
  // Last received events
  const [lastDeviceStatus, setLastDeviceStatus] = useState<DeviceStatusPayload | null>(null);
  const [lastSensorReading, setLastSensorReading] = useState<SensorReadingPayload | null>(null);
  const [lastValveAction, setLastValveAction] = useState<ValveActionPayload | null>(null);
  const [lastIrrigationEvent, setLastIrrigationEvent] = useState<IrrigationEventPayload | null>(null);

  // Track if we've shown connection toast
  const hasShownConnectionToast = useRef(false);

  const connect = useCallback(async () => {
    try {
      setConnectionState('connecting');
      await websocketService.connect();
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      setConnectionState('disconnected');
    }
  }, []);

  const disconnect = useCallback(() => {
    websocketService.disconnect();
    setIsConnected(false);
    setConnectionState('disconnected');
  }, []);

  // Handle connection status changes
  useEffect(() => {
    const unsubscribe = websocketService.on<{ connected: boolean }>('connection.status', (payload) => {
      setIsConnected(payload.connected);
      setConnectionState(payload.connected ? 'connected' : 'disconnected');
      
      // Show toast on connection change (but not on initial disconnect)
      if (payload.connected && !hasShownConnectionToast.current) {
        hasShownConnectionToast.current = true;
        toast({
          title: 'Conectado',
          description: 'Conexión en tiempo real establecida',
        });
      } else if (!payload.connected && hasShownConnectionToast.current) {
        toast({
          title: 'Desconectado',
          description: 'Conexión en tiempo real perdida. Reintentando...',
          variant: 'destructive',
        });
      }
    });

    return () => unsubscribe();
  }, [toast]);

  // Handle device status updates
  // Usar requestAnimationFrame para evitar problemas de inicialización
  useEffect(() => {
    const unsubscribe = websocketService.on<DeviceStatusPayload>('device.status', (payload) => {
      setLastDeviceStatus(payload);
      
      // Invalidate relevant queries to refresh data usando requestAnimationFrame
      requestAnimationFrame(() => {
        if (payload.deviceType === 'controller') {
          queryClient.invalidateQueries({ queryKey: ['controllers'] });
        } else if (payload.deviceType === 'sensor') {
          queryClient.invalidateQueries({ queryKey: ['sensors'] });
        }
      });
      
      // Show toast for status changes
      if (payload.status === 'offline') {
        toast({
          title: 'Dispositivo desconectado',
          description: `El ${payload.deviceType === 'controller' ? 'controlador' : 'sensor'} se ha desconectado`,
          variant: 'destructive',
        });
      }
    });

    return () => unsubscribe();
  }, [queryClient, toast]);

  // Handle sensor readings
  // Usar requestAnimationFrame para evitar problemas de inicialización
  useEffect(() => {
    const unsubscribe = websocketService.on<SensorReadingPayload>('sensor.reading', (payload) => {
      setLastSensorReading(payload);
      
      // Invalidate sensor data queries usando requestAnimationFrame
      requestAnimationFrame(() => {
        queryClient.invalidateQueries({ queryKey: ['sensors'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      });
    });

    return () => unsubscribe();
  }, [queryClient]);

  // Handle valve actions - CRITICAL: This is the source of truth for valve status
  // Usar requestAnimationFrame para evitar problemas de inicialización
  useEffect(() => {
    const unsubscribe = websocketService.on<ValveActionPayload>('valve.action', (payload) => {
      console.log('[WebSocket] 🔔 valve.action received:', payload);
      setLastValveAction(payload);
      
      // Invalidate controller data queries to refresh UI usando requestAnimationFrame
      requestAnimationFrame(() => {
        queryClient.invalidateQueries({ queryKey: ['controllers'] });
      });
      
      // Show toast for valve actions
      toast({
        title: payload.action === 'open' ? '✅ Válvula abierta' : '⏹️ Válvula cerrada',
        description: `${payload.controllerId.slice(0, 8)}... CH${payload.valveChannel} - ${payload.triggeredBy === 'manual' ? 'Manual' : payload.triggeredBy === 'schedule' ? 'Programado' : 'Trigger'}`,
      });
    });

    return () => unsubscribe();
  }, [queryClient, toast]);

  // Handle irrigation events
  // Usar requestAnimationFrame para evitar problemas de inicialización
  // NOTA: No invalidamos ['gardens'] aquí porque Gardens usa polling, no WebSocket
  useEffect(() => {
    const unsubscribe = websocketService.on<IrrigationEventPayload>('irrigation.event', (payload) => {
      setLastIrrigationEvent(payload);
      
      // Solo invalidar dashboard-stats, NO gardens (Gardens usa polling)
      requestAnimationFrame(() => {
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
        // NO invalidar ['gardens'] - Gardens page usa polling cada 5 minutos
      });
      
      // Show toast for irrigation events
      const eventMessages: Record<string, string> = {
        start: 'Riego iniciado',
        stop: 'Riego finalizado',
        pause: 'Riego pausado',
        resume: 'Riego reanudado',
      };
      
      toast({
        title: eventMessages[payload.eventType] || 'Evento de riego',
        description: `Fuente: ${payload.source === 'manual' ? 'Manual' : payload.source === 'schedule' ? 'Programación' : 'Trigger'}`,
      });
    });

    return () => unsubscribe();
  }, [queryClient, toast]);

  // Auto-connect when authenticated - con delay mayor para evitar problemas de inicialización
  useEffect(() => {
    if (isAuthenticated) {
      // Usar setTimeout con delay mayor para dar tiempo a que todos los componentes se inicialicen
      const connectTimer = setTimeout(() => {
        connect().catch((error) => {
          console.error('[WebSocket] Failed to connect:', error);
        });
      }, 2000); // Esperar 2 segundos después de la autenticación para mayor seguridad
      
      return () => {
        clearTimeout(connectTimer);
        disconnect();
      };
    } else {
      disconnect();
      hasShownConnectionToast.current = false;
    }
  }, [isAuthenticated, connect, disconnect]);

  const value: WebSocketContextType = {
    isConnected,
    connectionState,
    connect,
    disconnect,
    lastDeviceStatus,
    lastSensorReading,
    lastValveAction,
    lastIrrigationEvent,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket(): WebSocketContextType {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
